import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  Target,
  Sparkles,
  HelpCircle,
  Plus,
  X,
  Lightbulb,
  AlertTriangle,
} from "lucide-react";
import { GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";

// Types
interface UbdGoalsQuestionsStepProps {
  planId?: number;
  ubdUnderstandingGoals: string[];
  ubdTransferGoals: string[];
  ubdEssentialQuestions: string[];
  isLoading: boolean;
  isGenerateAllMode?: boolean;
  onGenerateWithAI: (
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => void;
  onFieldChange: (field: string, value: string[] | {}) => void;
}

const UbdGoalsQuestionsStep: React.FC<UbdGoalsQuestionsStepProps> = ({
  planId,
  ubdUnderstandingGoals,
  ubdTransferGoals,
  ubdEssentialQuestions,
  isLoading,
  onGenerateWithAI,
  onFieldChange,
}) => {
  // Initialize arrays with proper split - no need for ID system
  const initializeArray = useCallback((contentArray: string[]): string[] => {
    if (!contentArray || contentArray.length === 0) {
      return ["", "", ""];
    }
    return contentArray.map((content) => content.trim());
  }, []);

  // Initialize arrays
  const [understandingGoals, setUnderstandingGoals] = useState<string[]>(() =>
    initializeArray(ubdUnderstandingGoals),
  );
  const [transferGoals, setTransferGoals] = useState<string[]>(() =>
    initializeArray(ubdTransferGoals),
  );
  const [essentialQuestions, setEssentialQuestions] = useState<string[]>(() =>
    initializeArray(ubdEssentialQuestions),
  );

  // Popover state for warning
  const [showWarningPopover, setShowWarningPopover] = useState(false);

  // Check if there's existing data
  const hasExistingData = useCallback(() => {
    const hasUnderstandingGoals = understandingGoals.some(
      (goal) => goal.trim() !== "",
    );
    const hasTransferGoals = transferGoals.some((goal) => goal.trim() !== "");
    const hasEssentialQuestions = essentialQuestions.some(
      (question) => question.trim() !== "",
    );

    return hasUnderstandingGoals || hasTransferGoals || hasEssentialQuestions;
  }, [understandingGoals, transferGoals, essentialQuestions]);

  // Handle generate all with warning
  const handleGenerateAll = () => {
    if (hasExistingData()) {
      setShowWarningPopover(true);
    } else {
      onGenerateWithAI("GOALS_AND_QUESTIONS");
    }
  };

  // Confirm generate all
  const confirmGenerateAll = () => {
    setShowWarningPopover(false);
    onGenerateWithAI("GOALS_AND_QUESTIONS");
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showWarningPopover && event.target instanceof Element) {
        const popover = document.querySelector(".warning-popover");
        const button = document.querySelector(".generate-all-button");

        if (
          popover &&
          !popover.contains(event.target) &&
          button &&
          !button.contains(event.target)
        ) {
          setShowWarningPopover(false);
        }
      }
    };

    if (showWarningPopover) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showWarningPopover]);

  useEffect(() => {
    if (
      ubdUnderstandingGoals.length > 0 ||
      ubdTransferGoals.length > 0 ||
      ubdEssentialQuestions.length > 0
    ) {
      if (understandingGoals.toString() !== ubdUnderstandingGoals.toString()) {
        setUnderstandingGoals(initializeArray(ubdUnderstandingGoals));
      }
      if (transferGoals.toString() !== ubdTransferGoals.toString()) {
        setTransferGoals(initializeArray(ubdTransferGoals));
      }
      if (essentialQuestions.toString() !== ubdEssentialQuestions.toString()) {
        setEssentialQuestions(initializeArray(ubdEssentialQuestions));
      }
    }
  }, [
    ubdUnderstandingGoals,
    ubdTransferGoals,
    ubdEssentialQuestions,
    initializeArray,
  ]);

  // Array update functions
  const updateUnderstandingGoal = useCallback(
    (index: number, content: string) => {
      setUnderstandingGoals((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdUnderstandingGoals", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const updateTransferGoal = useCallback(
    (index: number, content: string) => {
      setTransferGoals((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdTransferGoals", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const updateEssentialQuestion = useCallback(
    (index: number, content: string) => {
      setEssentialQuestions((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdEssentialQuestions", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // Add functions
  const addUnderstandingGoal = useCallback(() => {
    setUnderstandingGoals((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdUnderstandingGoals", updated);
      return updated;
    });
  }, [onFieldChange]);

  const addTransferGoal = useCallback(() => {
    setTransferGoals((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdTransferGoals", updated);
      return updated;
    });
  }, [onFieldChange]);

  const addEssentialQuestion = useCallback(() => {
    setEssentialQuestions((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdEssentialQuestions", updated);
      return updated;
    });
  }, [onFieldChange]);

  // Remove functions
  const removeUnderstandingGoal = useCallback(
    (index: number) => {
      setUnderstandingGoals((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdUnderstandingGoals", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const removeTransferGoal = useCallback(
    (index: number) => {
      setTransferGoals((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdTransferGoals", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const removeEssentialQuestion = useCallback(
    (index: number) => {
      setEssentialQuestions((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdEssentialQuestions", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // AI generation functions
  const generateUnderstandingAI = () => {
    onGenerateWithAI("UNDERSTANDING_GOALS");
  };

  const generateTransferAI = () => {
    onGenerateWithAI("TRANSFER_GOALS");
  };

  const generateQuestionsAI = () => {
    onGenerateWithAI("ESSENTIAL_QUESTIONS");
  };

  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${Math.max(textarea.scrollHeight, 60)}px`;
  };

  // Auto-resize all textareas when data changes
  useEffect(() => {
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll("textarea");
      textareas.forEach((textarea) => {
        if (textarea.value) {
          autoResize(textarea as HTMLTextAreaElement);
        }
      });
    };

    // Small delay to ensure DOM is updated
    const timer = setTimeout(resizeAllTextareas, 0);

    return () => clearTimeout(timer);
  }, [understandingGoals, transferGoals, essentialQuestions]);

  // Render Goal Cards Function
  const renderGoalCards = (
    goals: string[],
    updateGoal: (index: number, content: string) => void,
    addGoal: () => void,
    removeGoal: (index: number) => void,
    generateAI: () => void,
    config: {
      title: string;
      icon: React.ReactNode;
      placeholder: string;
      description: string;
      colorClasses: {
        iconColor: string;
        cardBg: string;
        cardBorder: string;
        focusRing: string;
        aiButton: string;
        addButtonHover: string;
      };
    },
  ) => (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div
            className={`p-2 rounded-lg ${config.colorClasses.iconColor} shadow-sm`}
          >
            {config.icon}
          </div>
          <div>
            <Text
              variant="h3"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              {config.title}
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400 max-w-xs"
            >
              {config.description}
            </Text>
          </div>
        </div>

        {/* AI Generate Button */}
        <Button
          variant="solid"
          size="sm"
          onClick={generateAI}
          disabled={isLoading || !planId}
          className={`px-3 py-2 text-white text-xs rounded-lg transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-lg font-medium ${config.colorClasses.aiButton}`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent mr-2"></div>
              <span className="animate-pulse">...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3 mr-2" />
              AI
            </>
          )}
        </Button>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {goals.map((goal, index) => (
          <div
            key={index}
            className={`relative group ${config.colorClasses.cardBg} ${config.colorClasses.cardBorder} rounded-xl pl-2 shadow-sm hover:shadow-md transition-all duration-300`}
          >
            {/* Card Number */}
            <div className="absolute -top-2 -left-2 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-400 shadow-sm">
              {index + 1}
            </div>

            {/* Remove Button */}
            {goals.length > 1 && (
              <button
                onClick={() => removeGoal(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm hover:scale-110"
              >
                <X className="w-3 h-3" />
              </button>
            )}

            {/* Content Textarea */}
            <textarea
              value={goal}
              onChange={(e) => {
                updateGoal(index, e.target.value);
                autoResize(e.target);
              }}
              onInput={(e) => autoResize(e.target as HTMLTextAreaElement)}
              placeholder={config.placeholder}
              disabled={isLoading}
              className={`w-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 ${config.colorClasses.focusRing} rounded-lg p-2 min-h-[60px] ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              style={{ height: "auto" }}
              rows={1}
            />
          </div>
        ))}

        {/* Add New Card Button */}
        <button
          type="button"
          onClick={addGoal}
          disabled={isLoading}
          className={`w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl transition-all duration-300 ${config.colorClasses.addButtonHover} group disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
        >
          <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
            <Plus className="w-4 h-4" />
            <Text variant="span" size="sm" className="font-medium">
              Yeni Ekle
            </Text>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl shadow-md hover:scale-110 transition-transform duration-200">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <Text
              variant="h2"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              UBD Hedefler & Önemli Sorular
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400"
            >
              AI ile anlama ve transfer hedeflerinizi oluşturun
            </Text>
          </div>
        </div>

        <div className="relative">
          <Button
            variant="solid"
            onClick={handleGenerateAll}
            disabled={isLoading || !planId}
            className="generate-all-button px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white text-sm rounded-xl transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-xl font-semibold"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
                <span className="animate-pulse">Tümü Oluşturuluyor...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-3 animate-pulse" />
                Tümünü AI ile Oluştur
              </>
            )}
          </Button>

          {/* Warning Popover */}
          {showWarningPopover && (
            <div className="warning-popover absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50 animate-fade-in">
              {/* Triangle pointer */}
              <div className="absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                </div>
                <div className="flex-1">
                  <Text
                    variant="p"
                    className="text-gray-800 dark:text-gray-200 font-medium text-sm mb-2"
                  >
                    Mevcut İçeriği Değiştir
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-1"
                  >
                    Hedefler ve önemli sorular bölümlerinde mevcut içerik
                    bulunuyor.
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-3"
                  >
                    AI ile yeni içerik oluşturduğunuzda,{" "}
                    <strong>tüm mevcut içerikleriniz silinecek</strong> ve
                    yerine AI tarafından oluşturulan yeni içerik gelecektir.
                  </Text>
                  <div className="flex space-x-2">
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={() => setShowWarningPopover(false)}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      İptal
                    </Button>
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={confirmGenerateAll}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md"
                    >
                      Evet, Değiştir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {planId ? (
        <>
          {/* Colorful Goals Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Understanding Goals */}
            {renderGoalCards(
              understandingGoals,
              updateUnderstandingGoal,
              addUnderstandingGoal,
              removeUnderstandingGoal,
              generateUnderstandingAI,
              {
                title: "Anlama Hedefi",
                icon: (
                  <Target className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                ),
                placeholder:
                  "Öğrenciler... kavramını derinlemesine anlayacaklar",
                description:
                  "Öğrencilerin kavramları derinlemesine anlamasını sağlayan hedefler",
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 border border-blue-300 dark:border-blue-600",
                  cardBg:
                    "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/15 dark:to-indigo-900/15 border-2",
                  cardBorder:
                    "border-blue-400 dark:border-blue-500 hover:border-blue-500 dark:hover:border-blue-400",
                  focusRing:
                    "focus:border-blue-600 dark:focus:border-blue-300 focus:ring-4 focus:ring-blue-500/30 dark:focus:ring-blue-400/30",
                  aiButton:
                    "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 hover:border-blue-500",
                },
              },
            )}

            {/* Transfer Goals */}
            {renderGoalCards(
              transferGoals,
              updateTransferGoal,
              addTransferGoal,
              removeTransferGoal,
              generateTransferAI,
              {
                title: "Transfer Hedefi",
                icon: (
                  <Sparkles className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                ),
                placeholder:
                  "Öğrenciler... becerilerini farklı durumlarda kullanabilecekler",
                description:
                  "Öğrendiklerini farklı durumlarda uygulayabilme becerileri",
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-emerald-100 to-green-200 dark:from-emerald-900/40 dark:to-green-800/40 border border-emerald-300 dark:border-emerald-600",
                  cardBg:
                    "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/15 dark:to-green-900/15 border-2",
                  cardBorder:
                    "border-emerald-400 dark:border-emerald-500 hover:border-emerald-500 dark:hover:border-emerald-400",
                  focusRing:
                    "focus:border-emerald-600 dark:focus:border-emerald-300 focus:ring-4 focus:ring-emerald-500/30 dark:focus:ring-emerald-400/30",
                  aiButton:
                    "bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 hover:from-emerald-700 hover:via-emerald-800 hover:to-green-800 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-emerald-100 hover:to-green-100 dark:hover:from-emerald-900/30 dark:hover:to-green-900/30 hover:border-emerald-500",
                },
              },
            )}

            {/* Essential Questions */}
            {renderGoalCards(
              essentialQuestions,
              updateEssentialQuestion,
              addEssentialQuestion,
              removeEssentialQuestion,
              generateQuestionsAI,
              {
                title: "Önemli Sorular",
                icon: (
                  <HelpCircle className="w-4 h-4 text-orange-700 dark:text-orange-300" />
                ),
                placeholder: "... nasıl/neden/ne zaman?",
                description: "Düşünmeyi tetikleyen ve merak uyandıran sorular",
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-orange-100 to-amber-200 dark:from-orange-900/40 dark:to-amber-800/40 border border-orange-300 dark:border-orange-600",
                  cardBg:
                    "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/15 dark:to-amber-900/15 border-2",
                  cardBorder:
                    "border-orange-400 dark:border-orange-500 hover:border-orange-500 dark:hover:border-orange-400",
                  focusRing:
                    "focus:border-orange-600 dark:focus:border-orange-300 focus:ring-4 focus:ring-orange-500/30 dark:focus:ring-orange-400/30",
                  aiButton:
                    "bg-gradient-to-r from-orange-600 via-orange-700 to-amber-700 hover:from-orange-700 hover:via-orange-800 hover:to-amber-800 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-orange-100 hover:to-amber-100 dark:hover:from-orange-900/30 dark:hover:to-amber-900/30 hover:border-orange-500",
                },
              },
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔒</div>
          <Text
            variant="h3"
            className="text-xl font-bold text-gray-600 dark:text-gray-400 mb-2"
          >
            Plan Oluşturma Gerekli
          </Text>
          <Text variant="p" className="text-gray-500 dark:text-gray-400">
            UBD hedeflerinizi oluşturmak için önce "Programlar Arası Bileşenler"
            adımından plan oluşturmanız gerekir.
          </Text>
        </div>
      )}

      {/* UBD Design Tips */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-md transition-all duration-300">
        <div className="flex items-center space-x-2 mb-3">
          <Lightbulb className="w-4 h-4 text-yellow-500 animate-pulse" />
          <Text
            variant="h4"
            className="text-sm font-semibold text-gray-800 dark:text-gray-200"
          >
            UBD Tasarım İpuçları
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          {/* Blue Tips - Understanding */}
          <div className="space-y-1 bg-blue-50/70 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700 hover:bg-blue-100/70 dark:hover:bg-blue-900/30 transition-colors duration-200">
            <div className="flex items-center space-x-1">
              <Target className="w-3 h-3 text-blue-500" />
              <Text
                variant="span"
                className="font-medium text-blue-700 dark:text-blue-300 text-xs"
              >
                Anlama Hedefi
              </Text>
            </div>
            <ul className="space-y-0.5 text-gray-600 dark:text-gray-400 text-xs pl-4">
              <li>
                • Bu hedefler ders planınızda kavramsal öğrenme aktiviteleri
                olarak görünür
              </li>
              <li>
                • Öğrenci merkezli keşif ve tartışma etkinliklerine dönüşür
              </li>
              <li>
                • Değerlendirmede derinlemesine anlama ölçütleri oluşturur
              </li>
            </ul>
          </div>

          {/* Green Tips - Transfer */}
          <div className="space-y-1 bg-green-50/70 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-700 hover:bg-green-100/70 dark:hover:bg-green-900/30 transition-colors duration-200">
            <div className="flex items-center space-x-1">
              <Sparkles className="w-3 h-3 text-green-500" />
              <Text
                variant="span"
                className="font-medium text-green-700 dark:text-green-300 text-xs"
              >
                Transfer Hedefi
              </Text>
            </div>
            <ul className="space-y-0.5 text-gray-600 dark:text-gray-400 text-xs pl-4">
              <li>
                • Bu hedefler ders planınızda gerçek hayat senaryolarına dönüşür
              </li>
              <li>
                • Proje tabanlı öğrenme ve uygulama aktiviteleri oluşturur
              </li>
              <li>
                • Farklı bağlamlarda beceri kullanımını ölçen görevler sağlar
              </li>
            </ul>
          </div>

          {/* Orange Tips - Questions */}
          <div className="space-y-1 bg-orange-50/70 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-700 hover:bg-orange-100/70 dark:hover:bg-orange-900/30 transition-colors duration-200">
            <div className="flex items-center space-x-1">
              <HelpCircle className="w-3 h-3 text-orange-500" />
              <Text
                variant="span"
                className="font-medium text-orange-700 dark:text-orange-300 text-xs"
              >
                Önemli Sorular
              </Text>
            </div>
            <ul className="space-y-0.5 text-gray-600 dark:text-gray-400 text-xs pl-4">
              <li>
                • Bu sorular ders boyunca rehber görevi görür ve tartışmaları
                yönlendirir
              </li>
              <li>
                • Ünite sonunda öğrenci portfolyolarında yansıma soruları olur
              </li>
              <li>
                • Değerlendirme rubriklerinde üst düzey düşünme kriterleri
                sağlar
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UbdGoalsQuestionsStep;
